/**
 * WPS-119 - tells the RS whether the property they are claiming against is
 * already registered, and lets them attach the claim to it.
 *
 * Design notes (Clara Vale laws, and why each applies here):
 *
 * - **Decision-First.** When all of OC's criteria agree - owner name, owner
 *   phone, postcode and address - there is no decision left to make: it is the
 *   same property. So the matched state STATES an outcome rather than asking a
 *   question, and attaching is already done. The escape hatch is a quiet link,
 *   not a second button competing for attention.
 * - **Speed Is UX.** No modal, no confirm step, no extra click on the happy
 *   path. A modal would interrupt a form the RS is halfway through.
 * - **Teach While They Do.** The panel names WHY it matched ("owner name, phone
 *   and address"), so when a later claim does not match, the RS already
 *   understands the rule instead of filing a bug.
 * - **Progressive Disclosure.** The no-match case is one grey sentence with no
 *   icon and no border, because it is the common case and must not read as a
 *   problem. Only a real match earns a bordered panel.
 */

import { useTranslation } from 'react-i18next'
import { Building2, Check } from 'lucide-react'

import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import type { MatchingRegistration } from '@/admin/hooks/use-claims'

/** Ref string -> the words an RS would use. */
const REF_LABELS: Record<string, string> = {
  'field:consumer_name': 'owner name',
  'field:consumer_phone': 'phone',
  'field:consumer_email': 'email',
  'entity:host.address': 'address',
}

function describeRefs(refs: string[]): string {
  const words = refs.map((r) => REF_LABELS[r] ?? r.split(/[:.]/).pop() ?? r)
  if (words.length <= 1) return words[0] ?? ''
  return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`
}

export function RegistrationMatchPanel({
  match,
  isChecking,
  checkFailed = false,
  attach,
  onAttachChange,
}: {
  match: MatchingRegistration | null
  isChecking: boolean
  /**
   * The lookup ran and failed. Says so rather than showing nothing: a silent
   * fall-through would let a claim create a second registration for a property
   * already on file, which is the outcome this panel exists to prevent.
   */
  checkFailed?: boolean
  /** Whether the claim will attach to the matched registration. */
  attach: boolean
  onAttachChange: (attach: boolean) => void
}) {
  const { t } = useTranslation()

  // Nothing to say until the owner and address are in. Silence beats a
  // premature "no match" the RS has to mentally discard.
  if (isChecking) {
    return (
      <p className="m-0 mt-4 text-[12px] font-light text-[var(--ink-3)]">
        {t(
          'claims.registrationMatch.checking',
          'Checking whether this property is already registered...',
        )}
      </p>
    )
  }
  if (checkFailed) {
    return (
      <p className="m-0 mt-4 text-[12px] font-light text-[var(--ink-3)]">
        {t(
          'claims.registrationMatch.checkFailed',
          'We could not check whether this property is already registered. Submitting will create a new registration. Edit a field to try again.',
        )}
      </p>
    )
  }
  if (!match) return null

  if (!match.matched) {
    return (
      <p className="m-0 mt-4 text-[12px] font-light text-[var(--ink-3)]">
        {t(
          'claims.registrationMatch.noMatch',
          'No existing registration matches this owner and address. We will create one when you submit.',
        )}
      </p>
    )
  }

  const matchedOn = describeRefs(match.matched_on ?? [])

  return (
    <div className="mt-4 rounded-[8px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg-subtle)] p-4">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[var(--bg-card)]">
          <Building2 className="h-4 w-4 text-[var(--ink-2)]" aria-hidden />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="m-0 text-[13px] font-medium text-[var(--ink)]">
              {t(
                'claims.registrationMatch.alreadyRegistered',
                'This property is already registered',
              )}
            </p>
            {match.registration_number ? (
              <Badge variant="secondary">{match.registration_number}</Badge>
            ) : null}
          </div>

          <p className="m-0 mt-1 text-[12px] font-light text-[var(--ink-2)]">
            {[match.owner_name, match.owner_phone, match.product_name]
              .filter(Boolean)
              .join(' · ')}
          </p>

          {matchedOn ? (
            <p className="m-0 mt-1 text-[11px] font-light text-[var(--ink-3)]">
              {t('claims.registrationMatch.matchedOn', 'Matched on {{matchedOn}}.', {
                matchedOn,
              })}
            </p>
          ) : null}

          {attach ? (
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <span className="inline-flex items-center gap-1.5 text-[12px] font-normal text-[var(--ink)]">
                <Check className="h-3.5 w-3.5 text-[var(--status-ok)]" aria-hidden />
                {t(
                  'claims.registrationMatch.willBeAdded',
                  'This claim will be added to that registration',
                )}
              </span>
              <Button
                variant="link"
                size="xs"
                onClick={() => onAttachChange(false)}
              >
                {t(
                  'claims.registrationMatch.createInstead',
                  'Create a new registration instead',
                )}
              </Button>
            </div>
          ) : (
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <span className="text-[12px] font-normal text-[var(--ink-2)]">
                {t(
                  'claims.registrationMatch.willBeCreated',
                  'A new registration will be created for this claim.',
                )}
              </span>
              <Button
                variant="link"
                size="xs"
                onClick={() => onAttachChange(true)}
              >
                {t(
                  'claims.registrationMatch.useExisting',
                  'Use the existing registration',
                )}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
